import styled from 'styled-components';

const COBALT = '#0B4598';
const CORAL = '#FB7672';
const GREEN = '#13CA82';
const TEXT_DARK = '#1E2A3A';
const TEXT_MUTED = '#6B7280';
const BORDER = '#E5E7EB';
const SURFACE_MUTED = '#F3F5F7';
const READ_ONLY_BG = '#FDECEC';
const GREEN_PILL_BG = '#E8FAF1';
const MINT_PILL_BG = '#E6F8F2';
const PURPLE_PILL_BG = '#ECEAFB';
const PURPLE = '#6D5FD8';
const TEAL = '#1AB394';

export const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  background: #ffffff;
`;

export const Content = styled.div`
  flex: 1 1 auto;
  overflow: auto;
  padding: 12px;
`;

export const Inner = styled.div`
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const PaymentCardContainer = styled.div`
  border: 1px solid ${BORDER};
  border-radius: 16px;
  padding: 20px;
  background: #ffffff;
`;

export const SectionTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
  flex-wrap: wrap;
`;

export const SectionTitle = styled.h3`
  font-size: 17px;
  font-weight: 700;
  color: ${TEXT_DARK};
  margin: 0;
`;

export const ReadOnlyBadge = styled.span`
  background: ${READ_ONLY_BG};
  color: ${CORAL};
  font-size: 13px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 999px;
  line-height: 1.2;
`;

export const NoteText = styled.p`
  font-size: 14px;
  color: ${TEXT_MUTED};
  line-height: 1.5;
  margin: 0 0 16px;
`;

export const CardDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  border: 1px solid ${BORDER};
  border-radius: 12px;
  margin-bottom: 16px;
  background: #ffffff;
`;

export const CardBrandBox = styled.div`
  width: 54px;
  height: 40px;
  border: 1px solid ${BORDER};
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  flex-shrink: 0;

  img, svg {
    max-width: 40px;
    max-height: 26px;
  }
`;

export const CardBrandFallback = styled.div`
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: ${COBALT};
  text-align: center;
  padding: 0 2px;
`;

export const CardNumberBlock = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const CardNumber = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: ${TEXT_DARK};
  letter-spacing: 0.2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const CardExpiry = styled.div`
  font-size: 13px;
  color: ${TEXT_MUTED};
`;

export const StatusPill = styled.span<{ tone: 'active' | 'declined' | 'unknown' }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;

  ${({ tone }) => {
    if (tone === 'active') {
      return `background: ${GREEN_PILL_BG}; color: ${TEXT_DARK};`;
    }
    if (tone === 'declined') {
      return `background: ${READ_ONLY_BG}; color: ${TEXT_DARK};`;
    }
    return `background: ${SURFACE_MUTED}; color: ${TEXT_MUTED};`;
  }}

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${({ tone }) =>
    tone === 'active' ? GREEN : tone === 'declined' ? CORAL : '#888'};
  }
`;

export const TrustRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

export const TrustPill = styled.div<{ tone: 'mint' | 'purple' }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  color: ${TEXT_DARK};
  background: ${({ tone }) => (tone === 'mint' ? MINT_PILL_BG : PURPLE_PILL_BG)};

  svg { flex-shrink: 0; }
`;

export const InfoNote = styled.div`
  display: flex;
  gap: 10px;
  padding: 14px;
  background: ${SURFACE_MUTED};
  border-radius: 12px;
  margin-bottom: 18px;
  font-size: 13px;
  color: ${TEXT_MUTED};
  line-height: 1.5;

  svg {
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

export const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 20px;
  border-radius: 999px;
  border: none;
  background: ${COBALT};
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 120ms ease;
  max-width: 260px;

  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &:hover:not(:disabled) { opacity: 0.92; }

  svg { flex-shrink: 0; }
`;

export const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: 12px;
  margin-top: 4px;
`;

export const EditableSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-top: 18px;
  padding-top: 18px;
  border-top: 1px solid ${BORDER};
`;

export const SecureLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  font-size: 12px;
  color: ${TEXT_MUTED};
`;

export const PaymentElementBox = styled.div`
  border: 1px solid ${BORDER};
  padding: 14px;
  border-radius: 12px;
  background: #ffffff;
`;

export const ErrorMessage = styled.div`
  color: ${CORAL};
  font-size: 13px;
  margin-top: 4px;
`;

export const NoCardText = styled.div`
  color: ${TEXT_MUTED};
  font-size: 14px;
  padding: 16px 0;
  text-align: center;
`;

export const Colors = {
  COBALT,
  CORAL,
  GREEN,
  TEAL,
  PURPLE,
  TEXT_DARK,
  TEXT_MUTED,
  BORDER,
};
